import { useEffect, useMemo, useRef } from 'react';
import { Box, Flex, Text, Tooltip } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import i18n from '../../i18n';
import { monthShort } from '../../utils/intlFormatters';
import { FALLBACK_CATEGORY_COLOR } from './CategoriesPage';

// SHORT_MONTHS is exported for other modules — kept as a getter so it picks up
// language changes at call time rather than import time.
export const SHORT_MONTHS = new Proxy([], {
  get(_target, prop) {
    const months = i18n.t('months.short', { ns: 'dates', returnObjects: true });
    return months[prop];
  },
});

const getMonthInitial = (monthIdx) => {
  const short = monthShort(monthIdx);
  return (short[0] || '').toUpperCase();
};

const BAR_WIDTH_BY_CURRENCY_COUNT = { 1: 10, 2: 8 };
const BAR_WIDTH_FALLBACK = 6;
const BAR_INTRA_GAP = 2;
const SLOT_MIN_WIDTH = 22;
const SLOT_STRIDE_GAP = 2;
const BAR_MAX_HEIGHT = 55;

const CURRENCY_OPACITY_SCALE = [1, 0.55, 0.35, 0.25];
const SINGLE_CURRENCY_OPACITY = 0.85;

function getBarOpacity(currencyIdx, totalCurrencies) {
  if (totalCurrencies <= 1) return SINGLE_CURRENCY_OPACITY;
  return CURRENCY_OPACITY_SCALE[currencyIdx] ?? 0.2;
}

function bucketMatches(bucket, categoryId) {
  return categoryId == null || bucket.category_id === categoryId;
}

export default function CategoryTimelineChart({
  monthlyTimeline,
  categoryId,
  color,
  selectedIdx,
  onSelect,
  currentMonthIdx,
}) {
  const { t } = useTranslation('categories');
  const scrollRef = useRef(null);
  const hasAutoScrolled = useRef(false);

  useEffect(() => { hasAutoScrolled.current = false; }, [categoryId]);

  const currencies = useMemo(() => {
    if (!monthlyTimeline) return [];
    const set = new Set();
    monthlyTimeline.forEach(m => {
      m.data.forEach(d => {
        if (bucketMatches(d, categoryId) && d.currency) set.add(d.currency);
      });
    });
    return [...set].sort();
  }, [monthlyTimeline, categoryId]);

  const slotSizing = useMemo(() => {
    const numCurrencies = Math.max(1, currencies.length);
    const barWidth = BAR_WIDTH_BY_CURRENCY_COUNT[numCurrencies] ?? BAR_WIDTH_FALLBACK;
    const slotInner = numCurrencies * barWidth + (numCurrencies - 1) * BAR_INTRA_GAP;
    const slotWidth = Math.max(SLOT_MIN_WIDTH, slotInner + 8);
    return { barWidth, slotWidth, slotStride: slotWidth + SLOT_STRIDE_GAP };
  }, [currencies.length]);
  const { barWidth, slotWidth, slotStride } = slotSizing;

  useEffect(() => {
    if (hasAutoScrolled.current || !scrollRef.current) return;
    if (currentMonthIdx <= 0) return;
    const target = currentMonthIdx * slotStride - scrollRef.current.clientWidth / 2 + slotStride / 2;
    scrollRef.current.scrollLeft = Math.max(0, target);
    hasAutoScrolled.current = true;
  }, [currentMonthIdx, slotStride]);

  const { timeline, maxPerCurrency } = useMemo(() => {
    const months = (monthlyTimeline || []).map(m => {
      const byCurrency = {};
      currencies.forEach(c => { byCurrency[c] = 0; });
      m.data.forEach(d => {
        if (!bucketMatches(d, categoryId)) return;
        const c = d.currency || 'COP';
        byCurrency[c] = (byCurrency[c] || 0) + Math.abs(d.total || 0);
      });
      return { year: m.year, month: m.month, byCurrency };
    });
    const maxes = {};
    currencies.forEach(c => {
      maxes[c] = Math.max(1, ...months.map(m => m.byCurrency[c] || 0));
    });
    return { timeline: months, maxPerCurrency: maxes };
  }, [monthlyTimeline, categoryId, currencies]);

  if (!monthlyTimeline || monthlyTimeline.length === 0) return null;

  const barColor = color || FALLBACK_CATEGORY_COLOR;

  return (
    <Box ref={scrollRef} overflowX="auto" sx={{ '&::-webkit-scrollbar': { display: 'none' }, scrollbarWidth: 'none' }}>
      <Box position="relative" w={`${timeline.length * slotStride}px`} minH="120px">
        <Flex align="flex-end" gap="2px" h="90px" pb="4px">
          {timeline.map((tlItem, i) => {
            const selected = selectedIdx === i;
            const totalVal = currencies.reduce((s, c) => s + (tlItem.byCurrency[c] || 0), 0);
            return (
              <Box
                as="button"
                key={`${tlItem.year}-${tlItem.month}`}
                display="flex"
                flexDirection="column"
                alignItems="center"
                w={`${slotWidth}px`}
                flexShrink={0}
                onClick={() => onSelect(i)}
                cursor="pointer"
              >
                {selected ? (
                  <Box w="14px" h="14px" borderRadius="sm" border="1.5px solid" borderColor="redesign.heading" mb="2px" />
                ) : (
                  <Box h="18px" />
                )}
                {totalVal > 0 ? (
                  <Flex align="flex-end" gap={`${BAR_INTRA_GAP}px`}>
                    {currencies.map((c, cIdx) => {
                      const val = tlItem.byCurrency[c] || 0;
                      const h = val > 0 ? Math.max(3, (val / maxPerCurrency[c]) * BAR_MAX_HEIGHT) : 0;
                      const bar = h > 0 ? (
                        <Box
                          w={`${barWidth}px`}
                          h={`${h}px`}
                          borderTopRadius="sm"
                          bg={barColor}
                          opacity={getBarOpacity(cIdx, currencies.length)}
                        />
                      ) : (
                        <Box w={`${barWidth}px`} h="2px" bg="redesign.border" borderRadius="sm" />
                      );
                      const label = val > 0
                        ? `${c} ${val.toLocaleString('en-US', { maximumFractionDigits: 0 })}`
                        : `${c} — ${t('timeline.noData')}`;
                      return (
                        <Tooltip
                          key={c}
                          label={label}
                          placement="top"
                          hasArrow
                          openDelay={150}
                          isDisabled={currencies.length <= 1}
                          fontSize="xs"
                          fontWeight="600"
                          borderRadius="full"
                          px={3}
                          py={1}
                        >
                          {bar}
                        </Tooltip>
                      );
                    })}
                  </Flex>
                ) : (
                  <Box w="14px" display="flex" alignItems="center" h="6px">
                    <Box w="100%" borderTop="2.5px dashed" borderColor="redesign.accent.green" opacity={0.6} />
                  </Box>
                )}
              </Box>
            );
          })}
        </Flex>
        <Flex gap="2px" mt={1}>
          {timeline.map((tlItem, i) => (
            <Box
              as="button"
              key={`${tlItem.year}-${tlItem.month}-lbl`}
              w={`${slotWidth}px`}
              flexShrink={0}
              textAlign="center"
              onClick={() => onSelect(i)}
            >
              <Text
                fontSize="xs"
                fontWeight={selectedIdx === i ? 'bold' : '500'}
                color={selectedIdx === i ? 'redesign.heading' : 'redesign.text.muted'}
              >
                {getMonthInitial(tlItem.month - 1)}
              </Text>
            </Box>
          ))}
        </Flex>
      </Box>
    </Box>
  );
}
